import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../../user/UserContext';
import { OrdersWootTableRow } from '../../WootOrdersImport.interfaces';
import { DateTime } from 'luxon';
import { apiFetch, apiFetchRaw } from '../../../utility/fetch';
import { useLoading } from '../../../loading/LoadingContext';
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError';
import WootFbaJourneyStep from './WootFbaJourneyStep';
import { getSalesforceUrlByWindowLocation } from '../../../common/common.entities';
import { Form } from 'react-bootstrap';

export default function WootFbaJourneyComplete(){
  const {userData} = useUserContext()
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [ordersWoot, setOrdersWoot]: [OrdersWootTableRow[], any] = useState(undefined)
  const [refresh, setRefresh]: [string, any] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})
  const [filter, setFilter]: [string, any] = useState(undefined)

  useEffect(() => {
    const load = async function(){
      changeStep('GATHERING COMPLETED JOURNEYS')
      toggleLoading(true)
      changeSubStep('Querying ordersWoot table')
      const ordersResponse = await apiFetch({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [
          {key: 'table', value: 'ordersWoot'},
          {key: 'filter', value: `PartitionKey eq 'woot-fba' and status eq 'complete'`}
        ]
      })
      if(!ordersResponse.ok){
        setError({
          closeError: () => setError({visible: false}),
          content: JSON.stringify(ordersResponse.body),
          description: `Failed to get WOOT! FBA orders from table.`,
          title: 'LOADING ERROR',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      setOrdersWoot(ordersResponse.body)

      toggleLoading(false)
    }
    load()

  }, [refresh])

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const handleDownloadFile = useCallback((e) => {
    const download = async function() {
      if(!e.currentTarget.dataset.path){
        return
      }

      const justFileName = e.currentTarget.dataset.path?.split('/').pop()
      const justExtension = justFileName.split('.')?.pop().toLowerCase()

      setError({visible: false})
      changeStep('Download File from Azure')
      changeSubStep(`Requesting data for ${e.currentTarget.dataset.path}`)
      toggleLoading(true)

      const response = await apiFetchRaw({
        endpoint: 'blob',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: e.currentTarget.dataset.path}
        ],
        fileExtension: justExtension
      })

      if(!response.ok){
        setError({
          content: JSON.stringify(response),
          description: `Failed download file ${justFileName} from Azure.`,
          title: 'Download Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      if (response.ok && response.body instanceof Blob) {
        downloadBlob(response.body, justFileName || 'download.xlsx');
      } else if(response.ok){
        downloadBlob(new Blob([response.body as any], { type: 'json' }), justFileName || 'download.xlsx');
      }
      toggleLoading(false)
    }
    download()
  }, [])

  const view = useMemo(() => {
    const divs = []
    for(const row of ordersWoot ?? []){
      if(filter && filter.trim().length > 0){
        if(
          !row.poNumber.toLowerCase().includes(filter.toLowerCase()) && 
          !row.wrpoNumber.toLowerCase().includes(filter.toLowerCase())
        ){
          continue
        }
      }

      const order = JSON.parse(row.returnOrder)
      divs.push(<div className='beacon-card'>
        <div className='beacon-header'>{row.poNumber} / {row.wrpoNumber}</div>
        <span className='beacon-label'>Invoice request sent to accounting on {DateTime.fromISO(row.invoiceTimestamp).setZone('America/Chicago').toLocaleString(DateTime.DATETIME_SHORT)}</span>
        <div style={{display: 'flex', flexWrap: 'wrap'}}>
          <WootFbaJourneyStep
            handleDownloadFile={handleDownloadFile}
            handlePreviewFile={undefined}
            isPreviewable={false}
            isSuccess={true}
            item={row.sourcePoPdfPath}
            itemRequiresParse={false}
            title='PO PDF'
          />
          <WootFbaJourneyStep
            handleDownloadFile={handleDownloadFile}
            handlePreviewFile={undefined}
            isPreviewable={false}
            isSuccess={true}
            item={row.sourcePoExcelPath}
            itemRequiresParse={false}
            title='PO EXCEL'
          />
          <WootFbaJourneyStep
            handleDownloadFile={handleDownloadFile}
            handlePreviewFile={undefined}
            isPreviewable={false}
            isSuccess={true}
            item={row.sourceWrpoPdfPath}
            itemRequiresParse={false}
            title='RPO PDF'
          />
          <WootFbaJourneyStep
            handleDownloadFile={handleDownloadFile}
            handlePreviewFile={undefined}
            isPreviewable={false}
            isSuccess={true}
            item={row.sourceWrpoPdfPath}
            itemRequiresParse={false}
            title='RPO EXCEL'
          />                              
        </div>
        <div className='beacon-header'>
          Original Purchase Order IA Groups:<br/>
          {JSON.parse(row.allocationIags).map(iag => (
            <div>
              <a target="_blank" rel="noopener noreferrer" href={`${getSalesforceUrlByWindowLocation(window.location.href)}/${iag.Id}`}>{iag.Shipment_Name__c}</a>
              <br/>
            </div>
          ))}
          <br/>
          Return Purchase Order IA Groups:<br/>
          {JSON.parse(row.returnIags).map(iag => (
            <div>
              <a target="_blank" rel="noopener noreferrer" href={`${getSalesforceUrlByWindowLocation(window.location.href)}/${iag.Id}`}>{iag.Shipment_Name__c}</a>
              <br/>
            </div>
          ))}
          <br/>
          Return Purchase Order Salesforce Order & Order Items: <a target="_blank" rel="noopener noreferrer" href={`${getSalesforceUrlByWindowLocation(window.location.href)}/${order.Id}`}>{order.Name}</a>
        </div>
      </div>)
    }

    return <div>
      <CustomError
        closeError={() => {setError({visible: false})}}
        content={error?.content}
        description={error?.description}
        title={error?.title}
        visible={error?.visible}
      />
      <div className='beacon-header'>
        Filter
        <Form.Control value={filter} onChange={(e) => {setFilter(e.currentTarget.value)}} type='text' placeholder='Enter a PO or WRPO Number...' />
      </div>
      <br/>
      {divs}
    </div>
  }, [ordersWoot, filter])

  return view
}